import { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AgGrid } from "@jsq/ds/core";
import {
  applyFiltersToGrid,
  applySortsToGrid,
  applyPaginationToGrid,
} from "~/pages/Billing/utils/agGridUtils";
import {
  deserializeFilters,
  deserializeSorts,
  parsePageParam,
  updateUrlParams,
  getUrlParams,
} from "~/pages/Billing/utils/urlParameterUtils";

export interface UseOverviewGridUrlSyncOptions<TFilter, TSort> {
  filtersToFilterModel: (filters: TFilter[] | null) => AgGrid.FilterModel | null;
  sortsToSortModel: (sorts: TSort[] | null) => AgGrid.SortModelItem[] | null;
  onFiltersChange?: (filters: TFilter[] | null) => void;
  onSortsChange?: (sorts: TSort[] | null) => void;
  onPageChange?: (page: number) => void;
}

export interface UseOverviewGridUrlSyncReturn<TFilter, TSort> {
  handleFiltersChange: (filters: TFilter[] | null) => void;
  handleSortsChange: (sorts: TSort[] | null) => void;
  onGridReady: (
    event: AgGrid.GridReadyEvent,
    originalHandler?: (event: AgGrid.GridReadyEvent) => void,
  ) => void;
  onFirstDataRendered: (event: AgGrid.FirstDataRenderedEvent) => void;
  onPaginationChanged: (event: AgGrid.PaginationChangedEvent) => void;
}

export const useOverviewGridUrlSync = <TFilter, TSort>(
  options: UseOverviewGridUrlSyncOptions<TFilter, TSort>,
): UseOverviewGridUrlSyncReturn<TFilter, TSort> => {
  const {
    filtersToFilterModel,
    sortsToSortModel,
    onFiltersChange,
    onSortsChange,
    onPageChange,
  } = options;

  const history = useHistory();
  const location = useLocation();

  const urlParams = getUrlParams(location);
  const initialFilters = deserializeFilters<TFilter>(urlParams.filters);
  const initialSorts = deserializeSorts<TSort>(urlParams.sorts);
  const initialPage = parsePageParam(urlParams.page);

  const [currentFilters, setCurrentFilters] = useState<TFilter[] | null>(initialFilters);
  const [currentSorts, setCurrentSorts] = useState<TSort[] | null>(initialSorts);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);

  useEffect(() => {
    updateUrlParams(history, location, currentFilters, currentSorts, currentPage);
  }, [history, location, currentFilters, currentSorts, currentPage]);

  const handleFiltersChange = useCallback(
    (filters: TFilter[] | null) => {
      setCurrentFilters(filters);
      setCurrentPage(0);
      onFiltersChange?.(filters);
    },
    [onFiltersChange],
  );

  const handleSortsChange = useCallback(
    (sorts: TSort[] | null) => {
      setCurrentSorts(sorts);
      setCurrentPage(0);
      onSortsChange?.(sorts);
    },
    [onSortsChange],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      onPageChange?.(page);
    },
    [onPageChange],
  );

  const onGridReady = useCallback(
    (
      event: AgGrid.GridReadyEvent,
      originalHandler?: (event: AgGrid.GridReadyEvent) => void,
    ) => {
      originalHandler?.(event);

      const initialFilterModel = filtersToFilterModel(initialFilters);
      applyFiltersToGrid(event.api, initialFilterModel);
      const initialSortModel = sortsToSortModel(initialSorts);
      applySortsToGrid(event.api, initialSortModel);
    },
    [initialFilters, initialSorts, filtersToFilterModel, sortsToSortModel],
  );

  const onFirstDataRendered = useCallback(
    (event: AgGrid.FirstDataRenderedEvent) => {
      if (initialPage > 0) {
        applyPaginationToGrid(event.api, initialPage);
      }
    },
    [initialPage],
  );

  const onPaginationChanged = useCallback(
    (event: AgGrid.PaginationChangedEvent) => {
      const newPageIndex = event.api.paginationGetCurrentPage();
      handlePageChange(newPageIndex);
    },
    [handlePageChange],
  );

  return {
    handleFiltersChange,
    handleSortsChange,
    onGridReady,
    onFirstDataRendered,
    onPaginationChanged,
  };
};
